import { ThemeEvents } from '@theme/events';

const CART_TOTAL_PRICE_MAX = Number.MAX_SAFE_INTEGER;
const AUTO_HIDE_DELAY = 5000;
const EMPTY_CART_HIDE_DELAY = 1600;
const CONTROLLER_KEY = '__corailShippingBarController';
const SHIPPING_BAR_SELECTOR = '[data-corail-shipping-bar]';
const CLOSE_BUTTON_SELECTOR = '[data-corail-shipping-close]';

/**
 * Formats an amount held in euro cents without converting it to a float.
 *
 * @param {number} cents
 * @returns {string}
 */
function formatEuroCents(cents) {
  const safeCents = Math.max(0, Math.min(CART_TOTAL_PRICE_MAX, Math.trunc(cents)));
  const euros = Math.floor(safeCents / 100).toLocaleString('fr-FR');
  const centimes = String(safeCents % 100).padStart(2, '0');

  return `${euros},${centimes} €`;
}

/**
 * Converts a Shopify Cart API amount to a safe integer number of cents.
 *
 * @param {unknown} value
 * @returns {number}
 */
function toCents(value) {
  const parsedValue = Number.parseInt(String(value), 10);

  if (!Number.isSafeInteger(parsedValue)) return 0;

  return Math.max(0, parsedValue);
}

/**
 * @returns {{
 *   connect: (shippingBar: HTMLElement) => void,
 *   disconnect: (shippingBar: HTMLElement) => void,
 * }}
 */
function createShippingBarController() {
  const state = {
    activeBar: /** @type {HTMLElement | null} */ (null),
    autoHideTimeout: /** @type {number | undefined} */ (undefined),
    cart: /** @type {{ totalCents: number, itemCount: number } | null} */ (null),
    cartUrl: '',
    dismissed: false,
    popupFrame: /** @type {number | undefined} */ (undefined),
    refreshId: 0,
    visible: false,
  };

  /**
   * Removes the active auto-hide timer so a fresh cart action can replace it.
   */
  function clearAutoHideTimer() {
    window.clearTimeout(state.autoHideTimeout);
    state.autoHideTimeout = undefined;
  }

  /**
   * Renders the cached cart state into the current Horizon section instance.
   */
  function renderShippingBar() {
    const shippingBar = state.activeBar;
    const cart = state.cart;

    if (!shippingBar || !cart) return;

    const message = shippingBar.querySelector('[data-corail-shipping-message]');
    const progress = shippingBar.querySelector('[data-corail-shipping-progress]');
    const progressFill = shippingBar.querySelector('[data-corail-shipping-progress-fill]');

    if (!message || !progress || !progressFill) return;

    const thresholdCents = toCents(shippingBar.dataset.thresholdCents);
    const cartIsEmpty = cart.itemCount === 0;
    const goalReached = !cartIsEmpty && (thresholdCents === 0 || cart.totalCents >= thresholdCents);
    const remainingCents = goalReached ? 0 : Math.max(0, thresholdCents - cart.totalCents);
    const progressPercent = goalReached
      ? 100
      : thresholdCents > 0
        ? Math.min(100, Math.floor((cart.totalCents * 100) / thresholdCents))
        : 0;
    const emptyMessage = shippingBar.dataset.emptyMessage || 'Your cart is empty';
    const goalReachedMessage = shippingBar.dataset.goalReachedMessage || 'You have free shipping';
    const remainingMessage = shippingBar.dataset.remainingMessage || 'Only [amount] until free shipping';
    const progressMessage = cartIsEmpty
      ? emptyMessage
      : goalReached
        ? goalReachedMessage
        : remainingMessage.replace('[amount]', formatEuroCents(remainingCents));

    message.textContent = progressMessage;
    progress.setAttribute('aria-valuemax', String(Math.max(1, thresholdCents)));
    progress.setAttribute('aria-valuenow', String(Math.min(cart.totalCents, Math.max(1, thresholdCents))));
    progress.setAttribute('aria-valuetext', progressMessage);
    progressFill.style.setProperty('--corail-shipping-progress', `${progressPercent}%`);
    shippingBar.toggleAttribute('data-goal-reached', goalReached);
    shippingBar.toggleAttribute('data-corail-shipping-visible', state.visible && !state.dismissed);
    shippingBar.hidden = !state.visible || state.dismissed;
  }

  /**
   * Hides the current bar immediately. A manual dismissal also invalidates an
   * in-flight refresh so an already-started cart:update cannot reopen it.
   *
   * @param {{ dismissed?: boolean }} [options]
   */
  function hideShippingBar({ dismissed = false } = {}) {
    state.dismissed = dismissed;
    state.visible = false;
    if (dismissed) state.refreshId += 1;
    clearAutoHideTimer();
    window.cancelAnimationFrame(state.popupFrame);

    const shippingBar = state.activeBar;
    if (!shippingBar) return;

    shippingBar.hidden = true;
    shippingBar.removeAttribute('data-corail-shipping-visible');
    shippingBar.removeAttribute('data-corail-shipping-popup');
  }

  /**
   * Replays the short add-to-cart animation without changing the bar design.
   *
   * @param {HTMLElement} shippingBar
   */
  function playAddPopup(shippingBar) {
    window.cancelAnimationFrame(state.popupFrame);
    shippingBar.removeAttribute('data-corail-shipping-popup');

    state.popupFrame = window.requestAnimationFrame(() => {
      if (state.activeBar !== shippingBar || !state.visible || state.dismissed) return;

      shippingBar.setAttribute('data-corail-shipping-popup', '');
    });
  }

  /**
   * Shows the bar for a bounded amount of time and restarts that interval.
   *
   * @param {{ isProductAdded: boolean }} action
   */
  function showShippingBar(action) {
    const cartIsEmpty = state.cart?.itemCount === 0;

    clearAutoHideTimer();
    state.dismissed = false;
    state.visible = true;
    state.autoHideTimeout = window.setTimeout(
      () => hideShippingBar(),
      cartIsEmpty ? EMPTY_CART_HIDE_DELAY : AUTO_HIDE_DELAY
    );

    const shippingBar = state.activeBar;
    if (!shippingBar) return;

    renderShippingBar();

    if (action.isProductAdded && !cartIsEmpty) {
      playAddPopup(shippingBar);
    } else {
      window.cancelAnimationFrame(state.popupFrame);
      shippingBar.removeAttribute('data-corail-shipping-popup');
    }
  }

  /**
   * Refreshes from Shopify's authoritative cart state after every Horizon cart
   * action. The request id prevents stale responses from overwriting newer UI.
   *
   * @param {CustomEvent} event
   */
  async function refreshFromCart(event) {
    const cartUrl = state.activeBar?.dataset.cartUrl || state.cartUrl;
    const eventData = event.detail?.data;
    const requestId = ++state.refreshId;

    if (!cartUrl || eventData?.didError) return;

    // A cart:update is a new user action, so it deliberately supersedes a
    // previous manual dismissal. Responses that began before a close are still
    // ignored because hideShippingBar increments refreshId.
    state.dismissed = false;

    try {
      const response = await fetch(cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();

      if (requestId !== state.refreshId) return;

      state.cart = {
        itemCount: toCents(cart.item_count),
        totalCents: toCents(cart.total_price),
      };

      showShippingBar({
        isProductAdded: eventData?.source === 'product-form-component',
      });
    } catch (error) {
      console.warn('CORAIL shipping bar could not refresh the cart.', error);
    }
  }

  /**
   * Keeps one delegated close listener alive across Shopify/Horizon re-renders.
   *
   * @param {MouseEvent} event
   */
  function handleClose(event) {
    if (!(event.target instanceof Element)) return;

    const closeButton = event.target.closest(CLOSE_BUTTON_SELECTOR);
    if (!closeButton) return;

    const shippingBar = closeButton.closest(SHIPPING_BAR_SELECTOR);
    if (!shippingBar || shippingBar !== state.activeBar) return;

    event.preventDefault();
    hideShippingBar({ dismissed: true });
  }

  document.addEventListener(ThemeEvents.cartUpdate, refreshFromCart);
  document.addEventListener('click', handleClose, { capture: true });

  return {
    connect(shippingBar) {
      state.activeBar = shippingBar;
      state.cartUrl = shippingBar.dataset.cartUrl || state.cartUrl;
      renderShippingBar();

      if (!state.visible || state.dismissed || !state.cart) {
        shippingBar.hidden = true;
      }
    },

    disconnect(shippingBar) {
      if (state.activeBar === shippingBar) state.activeBar = null;
    },
  };
}

const shippingBarController = window[CONTROLLER_KEY] || createShippingBarController();
window[CONTROLLER_KEY] = shippingBarController;

class CorailShippingBar extends HTMLElement {
  connectedCallback() {
    shippingBarController.connect(this);
  }

  disconnectedCallback() {
    shippingBarController.disconnect(this);
  }
}

if (!customElements.get('corail-shipping-bar')) {
  customElements.define('corail-shipping-bar', CorailShippingBar);
}
